import { useState, useEffect, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import makeStyles from '@mui/styles/makeStyles';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useDebounce } from 'react-use';

/**
 * User data structure returned from the backend API
 */
interface AzureGroupMember {
  displayName: string;
  mail: string;
  userName?: string;
}

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionName: {
    fontWeight: 500,
  },
  optionEmail: {
    fontSize: '0.85em',
    color: theme.palette.text.secondary,
  },
  loadingText: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  errorText: {
    fontSize: '0.85rem',
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
}));

/**
 * Schema for the AzureGroupMemberPicker field extension
 */
export interface AzureGroupMemberPickerSchema {
  title?: string;
  description?: string;
}

/**
 * UI Schema options for the AzureGroupMemberPicker
 */
interface AzureGroupMemberPickerUiSchema {
  'ui:options'?: {
    /** Azure Entra group object ID whose members are listed */
    groupId?: string;
    /** Placeholder text for the input field */
    placeholder?: string;
    /** Whether to return the full user object or just the email */
    returnFullObject?: boolean;
    /** Whether to allow multiple selections */
    multiple?: boolean;
  };
}

/**
 * AzureGroupMemberPicker - A scaffolder field extension for selecting members from an Azure AD group
 *
 * This component fetches all members of a specified Azure AD group and allows
 * selection via an autocomplete dropdown for use in scaffolder templates.
 */

export const AzureGroupMemberPicker = (
  props: FieldExtensionComponentProps<
    string | string[] | AzureGroupMember | AzureGroupMember[],
    AzureGroupMemberPickerSchema
  >,
) => {
  const { onChange, rawErrors = [], required, formData, schema } = props;
  const { uiSchema } = props;
  const uiOptions =
    (uiSchema as AzureGroupMemberPickerUiSchema)?.['ui:options'] ?? {};
  const groupId = uiOptions.groupId;

  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState<AzureGroupMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AzureGroupMember | null>(
    null,
  );

  const minSearchLength = 3;
  const debounceMs = 300;

  useDebounce(
    () => {
      setDebouncedSearch(inputValue);
    },
    debounceMs,
    [inputValue],
  );

  const fetchUsers = useCallback(async () => {
    if (debouncedSearch.length < minSearchLength) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('githubemu');

      const params = new URLSearchParams({ search: debouncedSearch });
      if (groupId) params.set('groupId', groupId);

      const response = await fetchApi.fetch(
        `${baseUrl}/azure-group-members?${params.toString()}`,
      );

      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }

      // const users: AzureGroupMember[] = await response.json();
      const data = await response.json();
      const users: AzureGroupMember[] = Array.isArray(data) ? data : [];
      setOptions(users);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, discoveryApi, fetchApi, groupId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (formData === undefined || formData === null) return;

    let user: AzureGroupMember | null = null;

    if (typeof formData === 'string') {
      user = {
        displayName: formData,
        mail: formData,
      };
    } else if (typeof formData === 'object' && !Array.isArray(formData)) {
      user = formData as AzureGroupMember;
    }

    if (!user?.mail) return;

    setSelectedUser(user);
    setInputValue(user.displayName || user.mail);

    setOptions(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];

      const exists = safePrev.some(opt => opt.mail === user!.mail);
      return exists ? safePrev : [user!, ...safePrev];
    });
  }, [formData]);

  const handleChange = (
    _event: React.ChangeEvent<{}>,
    value: AzureGroupMember | null,
  ) => {
    setSelectedUser(value);

    if (value) {
      onChange(value);
    } else {
      onChange(undefined);
    }
  };

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    setInputValue(value);
  };

  return (
    <div className={classes.container}>
      <Autocomplete
        value={selectedUser}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        loading={loading}
        filterOptions={x => x}
        getOptionLabel={option => option.displayName || option.mail}
        isOptionEqualToValue={(option, value) => option.mail === value.mail}
        renderOption={(optionProps, option) => (
          <li {...optionProps} key={option.mail}>
            <div className={classes.option}>
              <Typography className={classes.optionName}>
                {option.displayName}
                {option.userName ? ` (${option.userName})` : ''}
              </Typography>
              <Typography className={classes.optionEmail}>
                {option.mail}
              </Typography>
            </div>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={schema.title || 'Search group member'}
            required={required}
            error={rawErrors && rawErrors.length > 0}
            helperText={schema.description || (rawErrors && rawErrors[0])}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} color="inherit" />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={
          inputValue.length < minSearchLength
            ? `Type at least ${minSearchLength} characters to search`
            : 'No members found'
        }
        fullWidth
      />
    </div>
  );
};
